import { hasNavigator } from "@klarify/utilities";

/**
 * Assign the property from src to dest if it exists and is not null/undefined
 * @param propertyName The property name to assign
 * @param src The source object
 * @param dest The destination object
 */
function assignProperty(
	propertyName: string,
	src: object,
	dest: Record<string, unknown>,
) {
	if (propertyName in src && src[propertyName as keyof typeof src]) {
		dest[propertyName] = src[propertyName as keyof typeof src];
	}
}

/**
 * User Agent hints to provide the userAgentData API with for extra information
 */
const HIGH_ENTROPY_USER_AGENT_HINTS = [
	"architecture",
	"bitness",
	"formFactors",
	"fullVersionList",
	"model",
	"platformVersion",
	"uaFullVersion",
];

/**
 * Properties to scan in the navigator object
 */
const NAVIGATOR_PROPERTIES_TO_INCLUDE = [
	"appVersion",
	"deviceMemory",
	"hardwareConcurrency",
	"language",
	"languages",
	"maxTouchPoints",
	"onLine",
	"oscpu",
	"platform",
	"vendor",
	"webdriver",
];

/**
 * Properties to scan in the navigator.userAgentData object
 */
const NAVIGATOR_UADATA_PROPERTIES_TO_INCLUDE = ["brands", "mobile"];

/**
 * Properties to scan in the navigator.connection object
 */
const CONNECTION_DATA_KEYS = [
	"downlink",
	"downlinkMax",
	"effectiveType",
	"rtt",
	"saveData",
	"type",
];

/**
 * Regex to extract Windows platform version (typically X.0.0)
 */
const windowsPlatformVersionRegex = /(\d+)\.\d+(\.\d+)?/;

/**
 * Get the user's platform data
 * @returns The user's platform data as a JSON string, or null if not available
 */
export async function getAdvancedUserAgent() {
	if (!hasNavigator()) {
		return null;
	}
	// Always include userAgent
	const output: Record<string, unknown> = {
		userAgent: navigator.userAgent,
	};
	for (const property of NAVIGATOR_PROPERTIES_TO_INCLUDE) {
		// Assign properties from navigator
		assignProperty(property, navigator, output);
	}
	if ("connection" in navigator && navigator.connection) {
		// Assign properties from the connection object
		for (const key of CONNECTION_DATA_KEYS) {
			assignProperty(key, navigator.connection, output);
		}
	}
	try {
		const storageEstimate = await navigator.storage.estimate();
		output.storage = { ...storageEstimate };
	} catch {
		// Ignore errors
	}
	if ("userAgentData" in navigator && navigator.userAgentData) {
		// Can fetch advanced info

		// Not supported in typescript (yet), so we have to cast to any to check for getHighEntropyValues
		const uaData = navigator.userAgentData as {
			getHighEntropyValues?: (
				hints: string[],
			) => Promise<Record<string, unknown>>;
		};
		// Reference: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/getHighEntropyValues
		if ("getHighEntropyValues" in uaData && uaData.getHighEntropyValues) {
			// Fetch high entropy values - this object also includes low entropy values like brands and mobile, so we can just merge it in directly
			const highEntropyValues = await uaData.getHighEntropyValues(
				HIGH_ENTROPY_USER_AGENT_HINTS,
			);
			if (highEntropyValues.platform === "Windows") {
				// Windows specific platform version processing, since its internal versions don't match the brand name versions
				const platformVersion = highEntropyValues.platformVersion;
				let windowsVersion = "Unknown Windows Version";
				if (platformVersion && typeof platformVersion === "string") {
					const match = platformVersion.match(windowsPlatformVersionRegex);
					if (match?.[1]) {
						const majorVersion = Number.parseInt(match[1], 10);
						if (!Number.isNaN(majorVersion)) {
							if (majorVersion === 0) {
								// 0.0.0 is what win8 and earlier report, since those versions predate this versioning scheme
								windowsVersion = "Windows 8 or earlier";
							} else if (majorVersion < 13) {
								// Version 13 was the first windows 11 preview version, so it and anything later is windows 11
								windowsVersion = "Windows 10";
							} else {
								// At some point we may need to add another check for future versions, but windows 11 is the latest so far.
								windowsVersion = "Windows 11";
							}
						}
					}
				}
				output.windowsVersion = windowsVersion;
			}
			return JSON.stringify({
				...output,
				...highEntropyValues,
			});
		}
		for (const property of NAVIGATOR_UADATA_PROPERTIES_TO_INCLUDE) {
			// Assign properties from userAgentData that always exist
			assignProperty(property, navigator.userAgentData, output);
		}
	}
	return JSON.stringify(output);
}
